import { ReactNode } from 'react';
import styled from 'styled-components';
import { ReactComponent as BiToolIcon } from '@elementary/common-ui/assets/bi-tool.svg';
import { ReactComponent as CatalogIcon } from '@elementary/common-ui/assets/catalog-icon.svg';
import { ReactComponent as IncidentManagementIcon } from '@elementary/common-ui/assets/incident-management-tool-icon.svg';
import { ReactComponent as SlackIcon } from '@elementary/common-ui/assets/notification-tool-icon.svg';
import { ReactComponent as RepositoryIcon } from '@elementary/common-ui/assets/source-control-icon.svg';
import { ReactComponent as TicketingManagementIcon } from '@elementary/common-ui/assets/ticketing-management-tool-icon.svg';
import { ReactComponent as DataCatalogIcon } from '@elementary/common-ui/assets/data-catalog-icon.svg';
import { ReactComponent as EtlToolIcon } from '@elementary/common-ui/assets/etl-tool-icon.svg';
import { FlexColumn, AccordionDetails } from '@elementary/common-ui/components';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import useFlags from 'shared/hooks/useFlags';
import {
  DataCatalogMetaStore,
  FetchBiIntegrationApi,
  FetchDataStoresApi,
  TicketingIntegration,
  EtlToolType,
  IntegrationSummary,
} from 'shared/types';
import { space } from '@elementary/common-ui/style/utils';
import IntegrationBox from '../../shared/Integration/IntegrationBox';
import { DialogTypes } from './types';
import { MessagingIntegrationDetails } from './MessagingMultipleDestinations';
import { MESSAGING_INTEGRATION_TYPES } from './constants';

const BoxContainer = styled(FlexColumn)`
  gap: ${space('md')};
`;

type Props = {
  onDialogChange: (dialog: DialogTypes) => void;
  isSlackConnected?: boolean;
  isTeamsConnected?: boolean;
  isTeamsBotConnected?: boolean;
  isIncidentManagementConnected?: boolean;
  isBiToolConnected?: boolean;
  isRepositoryConnected?: boolean;
  isSlackLoading: boolean;
  isTeamsLoading: boolean;
  isIncidentManagementLoading: boolean;
  isBiToolLoading: boolean;
  isRepositoryLoading: boolean;
  isTicketingConnected: boolean;
  isTicketingLoading: boolean;
  supportedMetaStores: Array<DataCatalogMetaStore>;
  dataCatalogConfig?: FetchDataStoresApi['integrations'];
  isDataCatalogLoading?: boolean;
  biIntegrations?: FetchBiIntegrationApi[];
  biMultipleDestinationsRenderer: () => ReactNode;
  ticketingIntegrations?: TicketingIntegration[];
  ticketingMultipleDestinationsRenderer: () => ReactNode;
  messagingIntegrations?: MessagingIntegrationDetails[];
  messagingMultipleDestinationsRenderer: () => ReactNode;
  dataCatalogMultipleDestinationsRenderer: () => ReactNode;
  etlToolIntegrations?: Record<EtlToolType, IntegrationSummary | undefined>;
  isEtlToolLoading?: boolean;
  messagingRuleCount?: number;
  incidentManagementRuleCount?: number;
  isAtlanConnected?: boolean;
  isAtlanLoading?: boolean;
  hasDbtProjects?: boolean;
};

const IntegrationAccordionDetails = ({
  onDialogChange,
  isSlackConnected,
  isTeamsConnected,
  isTeamsBotConnected,
  isIncidentManagementConnected,
  isBiToolConnected,
  isRepositoryConnected,
  isTicketingConnected,
  isSlackLoading,
  isTeamsLoading,
  isIncidentManagementLoading,
  isBiToolLoading,
  isRepositoryLoading,
  isTicketingLoading,
  supportedMetaStores,
  dataCatalogConfig,
  isDataCatalogLoading,
  biIntegrations,
  biMultipleDestinationsRenderer,
  ticketingIntegrations,
  ticketingMultipleDestinationsRenderer,
  messagingIntegrations,
  messagingMultipleDestinationsRenderer,
  dataCatalogMultipleDestinationsRenderer,
  etlToolIntegrations,
  isEtlToolLoading,
  messagingRuleCount,
  incidentManagementRuleCount,
  isAtlanConnected,
  isAtlanLoading,
  hasDbtProjects = true,
}: Props) => {
  const { hasFlag } = useFlags();

  const allMessagingTypesConnected =
    messagingIntegrations?.length === MESSAGING_INTEGRATION_TYPES.length;

  return (
    <AccordionDetails>
      <BoxContainer>
        <IntegrationBox
          title="Messaging app"
          description="Send notifications about data issues"
          info={
            !messagingIntegrations?.length && messagingRuleCount
              ? `Used by ${pluralizeString(
                  messagingRuleCount,
                  'alert rule',
                  'alert rules',
                )}`
              : undefined
          }
          icon={<SlackIcon />}
          onOpen={() => onDialogChange('notification')}
          connected={
            isSlackConnected || isTeamsConnected || isTeamsBotConnected
          }
          disabled={isSlackLoading && isTeamsLoading}
          supportMultipleDestinations={!!messagingIntegrations?.length}
          multipleDestinationsRenderer={messagingMultipleDestinationsRenderer}
          onMultipleDestinationsConnectClick={() =>
            onDialogChange('notification')
          }
          hideAddButton={allMessagingTypesConnected}
          addText="Add messaging integration"
        />
        <IntegrationBox
          title="Incident management tool"
          description="Get alerted on critical data incidents"
          info={
            incidentManagementRuleCount
              ? `Used by ${pluralizeString(
                  incidentManagementRuleCount,
                  'alert rule',
                  'alert rules',
                )}`
              : undefined
          }
          icon={<IncidentManagementIcon />}
          onOpen={() => onDialogChange('incidentManagement')}
          connected={isIncidentManagementConnected}
          disabled={isIncidentManagementLoading}
        />
        {hasFlag('bi') && (
          <IntegrationBox
            title="BI tools"
            description="Extend lineage and impact analysis to your BI dashboards"
            addText="Add new BI connection"
            icon={<BiToolIcon />}
            onOpen={() => onDialogChange('createBiTool')}
            connected={isBiToolConnected}
            disabled={isBiToolLoading}
            supportMultipleDestinations={!!biIntegrations?.length}
            multipleDestinationsRenderer={biMultipleDestinationsRenderer}
            onMultipleDestinationsConnectClick={() =>
              onDialogChange('createBiTool')
            }
          />
        )}
        {hasFlag('code_project') && hasDbtProjects && (
          <IntegrationBox
            title="Code repository"
            description="Manage tests and metadata in code"
            icon={<RepositoryIcon />}
            onOpen={() => onDialogChange('repository')}
            connected={isRepositoryConnected}
            disabled={isRepositoryLoading}
          />
        )}
        <IntegrationBox
          title="Ticketing system"
          description="Connect Elementary to your ticketing system"
          addText="Add new ticketing system"
          icon={<TicketingManagementIcon />}
          onOpen={() => onDialogChange('ticketing')}
          connected={isTicketingConnected}
          disabled={isTicketingLoading}
          supportMultipleDestinations={!!ticketingIntegrations?.length}
          multipleDestinationsRenderer={ticketingMultipleDestinationsRenderer}
          onMultipleDestinationsConnectClick={() => onDialogChange('ticketing')}
        />
        <IntegrationBox
          title="Data catalog"
          description="Export Elementary metadata and health scores to your data catalog"
          icon={<CatalogIcon />}
          onOpen={() => onDialogChange('atlan')}
          connected={isAtlanConnected}
          disabled={isAtlanLoading}
        />
        {!!supportedMetaStores?.length && hasFlag('metastore') && (
          <IntegrationBox
            title="Metadata Layer"
            description="Connect Elementary to your metadata store"
            addText="Add new metadata store"
            icon={<DataCatalogIcon />}
            onOpen={() => onDialogChange('dataCatalog')}
            connected={!!dataCatalogConfig?.length}
            disabled={isDataCatalogLoading}
            supportMultipleDestinations={!!dataCatalogConfig?.length}
            multipleDestinationsRenderer={
              dataCatalogMultipleDestinationsRenderer
            }
            onMultipleDestinationsConnectClick={() =>
              onDialogChange('dataCatalog')
            }
          />
        )}
        <IntegrationBox
          title="ETL tool"
          description="Monitor ETL tool syncs and detect pipeline failures"
          icon={<EtlToolIcon />}
          connected={Object.values(etlToolIntegrations ?? {}).some(
            (t) => t?.enabled,
          )}
          disabled={isEtlToolLoading}
          onOpen={() => onDialogChange('fivetran')}
          connectedButtonText="Edit"
        />
      </BoxContainer>
    </AccordionDetails>
  );
};

export default IntegrationAccordionDetails;
